/** @format */

import moment from 'moment';
import i18next from 'i18next';
import { ExportToCsv } from 'export-to-csv';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import {  SimCardServiceTypeItem } from '../../../interfaces/collections/SimCardServiceInterface';

const _ = require('underscore');

interface ServiceListToCsvData {
    codeName: any;
    serviceType: any;
    useType: any;
    externalSystemId: any;
    description: any;
    groupId: any;
    singleShotFlag: any;
    cycle: any;
    packageType: any;
    generateCdr: any;
    cdrOnAmount: any;
}

const BME_GROUP_ID = 12;

export class ServiceListToCsv {
    private readonly itemLoader: ItemLoaders;

    constructor(itemLoader: ItemLoaders) {
        this.itemLoader = itemLoader;
    }

    exportToCsv = (orders: Array<Order>, filters: Array<Filter>): void => {
        const extendFilter = _.clone(filters);
        extendFilter.push({ name: 'pagination', value: 'false' });
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types')),
            'sim_card_service_types',
            extendFilter,
            orders,
            (items: { 'hydra:member': Array<SimCardServiceTypeItem>; 'hydra:totalItems': number }): void => {
                this.prepareCsv(items);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    prepareCsv = (items: { 'hydra:member': Array<SimCardServiceTypeItem>; 'hydra:totalItems': number }): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'sim_card_service_types' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'Component List',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('prov:list.service_code'),
                i18next.t('prov:list.service_type'),
                i18next.t('prov:list.service_usetype'),
                i18next.t('prov:list.service_external'),
                i18next.t('prov:list.service_desc'),
                i18next.t('prov:list.service_group'),
                i18next.t('prov:list.service_oneshhot'),
                i18next.t('prov:list.service_cycle'),
                i18next.t('prov:list.service_ussd'),
                i18next.t('prov:list.service_cdr'),
                i18next.t('prov:list.service_cdramount'),
            ],
        };
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvList);
    };

    numberToBoolean = (value: any): boolean => {
        return value !== -1;
    };

    translateUsageType = (usageType: any): string => {
        switch (usageType) {
            case 1:
                return i18next.t('prov:usage_type.prepaid');
            case 2:
                return i18next.t('prov:usage_type.postpaid');
            case 3:
                return i18next.t('prov:usage_type.postpaid_prepaid');
            default:
                return 'NaN';
        }
    };

    translatePackageType = (packageType: any): string => {
        switch (packageType) {
            case 1:
                return i18next.t('prov:package_type.data');
            case 2:
                return i18next.t('prov:package_type.roaming');
            case 3:
                return i18next.t('prov:package_type.single_shot');
            case 4:
                return i18next.t('prov:package_type.voice');
            default:
                return 'NaN';
        }
    };

    translateGroupId = (groupId: any): string => {
        return groupId === BME_GROUP_ID ? 'BME' : 'SDG';
    };

    prepareCsvData = (items: { 'hydra:member': Array<SimCardServiceTypeItem>; 'hydra:totalItems': number }): Array<ServiceListToCsvData> => {
        const csvList: Array<ServiceListToCsvData> = [];
        if (items['hydra:member'] && items['hydra:totalItems'] && items['hydra:totalItems'] > 0) {
            items['hydra:member'].map((item: SimCardServiceTypeItem) => {
                csvList.push({
                    codeName: item.codeName,
                    serviceType: item.serviceType,
                    useType: this.translateUsageType(item.useType),
                    externalSystemId: item.externalSystemId,
                    description: item.description,
                    groupId: this.translateGroupId(item.groupId),
                    singleShotFlag: this.numberToBoolean(item.singleShotFlag),
                    cycle: this.numberToBoolean(item.cycle),
                    packageType: this.translatePackageType(item.packageType),
                    generateCdr: this.numberToBoolean(item.generateCdr),
                    cdrOnAmount: this.numberToBoolean(item.cdrOnAmount),
                });
            });
        }
        return csvList;
    };
}
